import { useEffect, useState } from 'react';
import { Percent, Plus } from 'lucide-react';
import { financialApi, type CommissionRule } from '../api';
import { PageHeader } from '@/lib/ui';

const SCOPES = ['TENANT', 'FIELD_USER', 'ENGAGEMENT', 'ASSET_TYPE'] as const;

function money(minor: number, currency = 'AED'): string {
  return `${currency} ${(minor / 100).toFixed(2)}`;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CommissionRulesPage() {
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CommissionRule | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      setRules(await financialApi.listCommissionRules());
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="p-6">
      <PageHeader
        title="Commission Rules"
        icon={Percent}
        subtitle="Rules accrue when work units close. Multiple rules can match — they stack."
        actions={
          <button className="btn-primary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New rule
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded border border-border bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-accent text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Slug</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-right">Priority</th>
              <th className="px-3 py-2 text-left">Reward</th>
              <th className="px-3 py-2 text-left">Window</th>
              <th className="px-3 py-2 text-left">Enabled</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rules.map((r) => (
              <tr key={r.id} className="hover:bg-surface-accent">
                <td className="px-3 py-2 font-mono">{r.slug}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2">{r.scope}</td>
                <td className="px-3 py-2 text-right">{r.priority}</td>
                <td className="px-3 py-2">
                  {r.flat_amount_minor !== null && r.flat_amount_minor !== undefined
                    ? money(r.flat_amount_minor, r.currency)
                    : r.flat_percent !== null && r.flat_percent !== undefined
                      ? `${r.flat_percent}%`
                      : `${r.tiers.length} tiers`}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {r.effective_from} → {r.effective_to ?? '∞'}
                </td>
                <td className="px-3 py-2">
                  {r.is_enabled ? (
                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs text-emerald-300">
                      on
                    </span>
                  ) : (
                    <span className="rounded bg-slate-500/15 px-1.5 py-0.5 text-xs text-slate-300">
                      off
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button className="text-xs text-primary" onClick={() => setEditing(r)}>
                    edit
                  </button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No commission rules yet. Click <strong>New rule</strong> above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <RuleEditor
          initial={editing}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function RuleEditor({
  initial,
  onCancel,
  onSaved,
}: {
  initial: CommissionRule | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>(initial?.scope ?? 'TENANT');
  const [priority, setPriority] = useState(initial?.priority ?? 100);
  const [mode, setMode] = useState<'flat_amount' | 'flat_percent' | 'tiers'>(
    initial?.flat_amount_minor ? 'flat_amount' : initial?.flat_percent ? 'flat_percent' : 'tiers',
  );
  const [flatAmount, setFlatAmount] = useState(String(initial?.flat_amount_minor ?? ''));
  const [flatPercent, setFlatPercent] = useState(String(initial?.flat_percent ?? ''));
  const [tiersText, setTiersText] = useState(JSON.stringify(initial?.tiers ?? [], null, 2));
  const [effFrom, setEffFrom] = useState(initial?.effective_from ?? todayStr());
  const [effTo, setEffTo] = useState(initial?.effective_to ?? '');
  const [enabled, setEnabled] = useState(initial?.is_enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: Omit<CommissionRule, 'id'> = {
        slug,
        name,
        scope,
        priority,
        scope_ref_id: null,
        is_enabled: enabled,
        effective_from: effFrom,
        effective_to: effTo || null,
        currency: 'AED',
        applies_when: {},
        tiers: [],
        flat_amount_minor: null,
        flat_percent: null,
      };
      if (mode === 'flat_amount') body.flat_amount_minor = Number(flatAmount);
      else if (mode === 'flat_percent') body.flat_percent = Number(flatPercent);
      else body.tiers = JSON.parse(tiersText);
      if (initial) await financialApi.updateCommissionRule(initial.id, body);
      else await financialApi.createCommissionRule(body);
      onSaved();
    } catch (e) {
      setErr((e as Error).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-[640px] overflow-y-auto rounded border border-border bg-surface p-6">
        <h3 className="mb-4 text-lg font-semibold">
          {initial ? `Edit · ${initial.slug}` : 'New commission rule'}
        </h3>
        {err && (
          <div className="mb-3 rounded bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Slug">
            <input
              className="input w-full font-mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={!!initial}
            />
          </Field>
          <Field label="Name">
            <input
              className="input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Scope">
            <select
              className="input w-full"
              value={scope}
              onChange={(e) => setScope(e.target.value as CommissionRule['scope'])}
            >
              {SCOPES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <input
              type="number"
              className="input w-full"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            />
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              className="input w-full"
              value={effFrom}
              onChange={(e) => setEffFrom(e.target.value)}
            />
          </Field>
          <Field label="Effective to (optional)">
            <input
              type="date"
              className="input w-full"
              value={effTo}
              onChange={(e) => setEffTo(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Reward type
          </label>
          <div className="mt-1 flex gap-2">
            {(['flat_amount', 'flat_percent', 'tiers'] as const).map((m) => (
              <button
                key={m}
                className={`rounded border px-3 py-1 text-sm ${mode === m ? 'border-primary bg-primary/10' : 'border-border'}`}
                onClick={() => setMode(m)}
              >
                {m}
              </button>
            ))}
          </div>
          {mode === 'flat_amount' && (
            <Field label="Amount (minor units)">
              <input
                className="input w-full font-mono"
                value={flatAmount}
                onChange={(e) => setFlatAmount(e.target.value)}
              />
            </Field>
          )}
          {mode === 'flat_percent' && (
            <Field label="Percent (0–100)">
              <input
                className="input w-full font-mono"
                value={flatPercent}
                onChange={(e) => setFlatPercent(e.target.value)}
              />
            </Field>
          )}
          {mode === 'tiers' && (
            <Field label="Tiers (JSON array)">
              <textarea
                className="input w-full font-mono text-xs"
                rows={6}
                value={tiersText}
                onChange={(e) => setTiersText(e.target.value)}
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Each tier: {`{ "from": int, "to": int|null, "amount_minor"|"percent": N }`}
              </div>
            </Field>
          )}
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
